import mapboxgl from "mapbox-gl";

import { LayerIds } from "fond/layers";
import { GroupConfig } from "fond/types";
import { FilterConfiguration, LayerConfig, LayerStyle, SublayerConfig } from "fond/types/ProjectLayerConfig";

import { FamilialLayerConfig, groupConfigTemplate, layerConfigTemplate, sublayerTemplate } from "./configuration";

export enum BaseDataLayerId {
  ROOT = "multiProjectBaseDataGroup",
  ADDRESSES = "multiProjectAddressesLayer",
  ADDRESSES_RESIDENTIAL = "multiProjectAddressesResidentialSublayer",
  ADDRESSES_MDU = "multiProjectAddressesMDUSublayer",
  ADDRESSES_COMMERCIAL = "multiProjectAddressesCommercialSublayer",
  ADDRESSES_OTHER = "multiProjectAddressesOtherSublayer",
  STREETS = "multiProjectStreetsLayer",
  STREETS_HIGHWAY = "multiProjectStreetsHighwaySublayer",
  STREETS_ARTERIAL = "multiProjectStreetsArterialSublayer",
  STREETS_RESIDENTIAL = "multiProjectStreetsResidentialSublayer",
  STREETS_SERVICE = "multiProjectStreetsServiceSublayer",
  STREETS_OTHER = "multiProjectStreetsOtherSublayer",
}

/**
 * Create the Base Data configuration.
 */
export const baseDataConfiguration = (): { configs: Array<LayerConfig | LayerStyle | SublayerConfig>; groupConfig: GroupConfig } => {
  const { config: addressesConfig, descendants: addressesDescendants } = createAddressesLayer();
  const { config: streetsConfig, descendants: streetsDescendants } = createStreetsLayer();

  const baseDataGroup = groupConfigTemplate(BaseDataLayerId.ROOT, [addressesConfig, streetsConfig], "Base Data", true, "multiProjectOverlayGroup");

  return {
    configs: [...addressesDescendants, addressesConfig, ...streetsDescendants, streetsConfig],
    groupConfig: baseDataGroup,
  };
};

const createAddressesLayer = (): FamilialLayerConfig => {
  // Define sublayers with their filters and colors
  const sublayers = [
    {
      id: BaseDataLayerId.ADDRESSES_RESIDENTIAL,
      label: "Residential, 1-4 Units",
      color: "#2563EB",
      addressTypes: ["SFU", "DPX", "TPX", "QPX"],
    },
    {
      id: BaseDataLayerId.ADDRESSES_MDU,
      label: "Residential, 5+ Units",
      color: "#10B981",
      addressTypes: ["SMDU", "MMDU", "LMDU"],
    },
    {
      id: BaseDataLayerId.ADDRESSES_COMMERCIAL,
      label: "Commercial",
      color: "#ffa203",
      addressTypes: ["MCU", "LCU", "SCU"],
    },
    {
      id: BaseDataLayerId.ADDRESSES_OTHER,
      label: "Other",
      color: "#b8337c",
      addressTypes: ["GOVT", "INSTITUTION", "PUBLIC", "OTHER", "DNS"],
    },
  ];

  // Create sublayers and their styles
  const descendants = sublayers.flatMap((sublayer) => {
    const mapboxFilter: mapboxgl.Expression = ["in", ["get", "AddressType"], ["literal", sublayer.addressTypes]];
    const filter: FilterConfiguration = {
      Mapbox: mapboxFilter,
      Type: "expression",
    };

    const styles = addressesLayerStyles(sublayer.id, sublayer.color, mapboxFilter);
    const sublayerConfig = sublayerTemplate({
      id: sublayer.id,
      key: LayerIds.inAddress,
      layerId: BaseDataLayerId.ADDRESSES,
      label: sublayer.label,
      filter,
      styles,
      geometryType: "Point",
    });

    return [...styles, sublayerConfig];
  });

  // Create main layer config with references to sublayers
  const config = layerConfigTemplate({
    id: BaseDataLayerId.ADDRESSES,
    key: LayerIds.inAddress,
    label: "Addresses",
    styles: [],
    children: sublayers.map((s) => s.id),
    parentId: BaseDataLayerId.ROOT,
    geometryType: "Point",
  });

  return { config, descendants };
};

const createStreetsLayer = (): FamilialLayerConfig => {
  // Define sublayers with their filters and colors
  const sublayers = [
    {
      id: BaseDataLayerId.STREETS_HIGHWAY,
      label: "Highway",
      color: "#5a5a5a",
      highwayTypes: ["motorway", "trunk"],
      // Always visible
    },
    {
      id: BaseDataLayerId.STREETS_ARTERIAL,
      label: "Arterial",
      color: "#e36c6e",
      highwayTypes: ["primary", "secondary", "tertiary"],
      // Fade in from zoom 6 to 7
      fadeZoom: { start: 6, end: 7 },
    },
    {
      id: BaseDataLayerId.STREETS_RESIDENTIAL,
      label: "Residential",
      color: "#e8a168",
      highwayTypes: ["residential", "living_street"],
      // Fade in from zoom 9 to 10
      fadeZoom: { start: 9, end: 10 },
    },
    {
      id: BaseDataLayerId.STREETS_SERVICE,
      label: "Service",
      color: "#f0d776",
      highwayTypes: ["service"],
      // Fade in from zoom 12 to 13
      fadeZoom: { start: 12, end: 13 },
    },
    {
      id: BaseDataLayerId.STREETS_OTHER,
      label: "Other",
      color: "#ea7be5",
      highwayTypes: ["unclassified"],
      // Fade in from zoom 9 to 10
      fadeZoom: { start: 9, end: 10 },
    },
  ];

  // Create sublayers and their styles
  const descendants = sublayers.flatMap((sublayer) => {
    const mapboxFilter: mapboxgl.Expression = ["in", ["get", "highway"], ["literal", sublayer.highwayTypes]];
    const filter: FilterConfiguration = {
      Mapbox: mapboxFilter,
      Type: "expression",
    };

    const styles = streetsLayerStyles(sublayer.id, sublayer.color, mapboxFilter, sublayer.fadeZoom);
    const sublayerConfig = sublayerTemplate({
      id: sublayer.id,
      key: LayerIds.inBaseDataStreet,
      layerId: BaseDataLayerId.STREETS,
      label: sublayer.label,
      filter,
      styles,
      geometryType: "LineString",
    });

    return [...styles, sublayerConfig];
  });

  // Create main layer config with references to sublayers
  const config = layerConfigTemplate({
    id: BaseDataLayerId.STREETS,
    key: LayerIds.inBaseDataStreet,
    label: "Streets",
    styles: [],
    children: sublayers.map((s) => s.id),
    parentId: BaseDataLayerId.ROOT,
    geometryType: "LineString",
  });

  return { config, descendants };
};

const addressesLayerStyles = (layerId: string, color: string, filter?: mapboxgl.Expression): LayerStyle[] => [
  {
    ID: `${layerId}-point`,
    Name: `${layerId}-point`,
    GlobalPosition: 1,
    ConfigurationID: layerId,
    ConfigurationType: "LAYER",
    Position: 0,
    MapboxStyle: {
      type: "circle",
      "source-layer": "default",
      ...(filter ? { filter } : {}),
      paint: {
        "circle-radius": 3,
        "circle-color": color,
      },
    },
    RawStyles: {
      Type: "circle",
      CircleRadius: 3,
      CircleColor: color,
    },
    Type: "STYLE",
  },
];

const streetsLayerStyles = (
  layerId: string,
  color: string,
  filter?: mapboxgl.Expression,
  fadeZoom?: { start: number; end: number }
): LayerStyle[] => {
  // Create opacity expression based on zoom level
  const opacityExpression: mapboxgl.Expression | number = fadeZoom ? ["interpolate", ["linear"], ["zoom"], fadeZoom.start, 0, fadeZoom.end, 1] : 1;

  return [
    {
      ID: `${layerId}-line`,
      Name: `${layerId}-line`,
      GlobalPosition: 1,
      ConfigurationID: layerId,
      ConfigurationType: "LAYER",
      Position: 0,
      MapboxStyle: {
        type: "line",
        "source-layer": "default",
        ...(filter ? { filter } : {}),
        paint: {
          "line-width": 2,
          "line-color": color,
          "line-opacity": opacityExpression,
        },
      },
      RawStyles: {
        Type: "line",
        LineColor: color,
        LineWidth: 2,
      },
      Type: "STYLE",
    },
  ];
};
